import { chunk } from "lodash-es"
import type HttpClient from "../../tastyworks/http"
import { MARKET_DATA_DESER, MarketData } from "../../tastyworks/market-data"
import { ItemsResponse, parseItemsResponse } from "../../tastyworks/response"

// The /market-data/by-type endpoint accepts a combined limit of 100 symbols
// across all instrument types, so batch larger requests.
const MARKET_DATA_BATCH_SIZE = 100

export class MarketDataService {
  private readonly marketDataClient: HttpClient

  constructor(baseHttpClient: HttpClient) {
    this.marketDataClient = baseHttpClient.nested("market-data")
  }

  // Snapshot bid/ask for a set of event-contract streamer symbols. The endpoint
  // expects bare repeated query keys (event-contract=A&event-contract=B); the
  // shared Params serializer would dasherize and append "[]" to an array, so we
  // build the query string by hand. Symbols are opaque backend strings — pass
  // them through verbatim, only URL-encoding the value.
  readonly byEventContractType = async (
    symbols: string[]
  ): Promise<ItemsResponse<MarketData>> => {
    const batches = chunk(symbols, MARKET_DATA_BATCH_SIZE)
    const responses = await Promise.all(batches.map(this.fetchBatch))
    const merged = new ItemsResponse<MarketData>()
    for (const response of responses) {
      merged.items.push(...response.items)
      // A 4xx batch comes back as an error response rather than throwing, so
      // without this the merge would silently report success with only the
      // surviving batches' items. Surface the first error so callers can tell
      // the snapshot was partial.
      if (response.isError && merged.isValid) {
        merged.errorContainer = response.errorContainer
      }
    }
    return merged
  }

  private readonly fetchBatch = async (
    symbols: string[]
  ): Promise<ItemsResponse<MarketData>> => {
    const query = symbols
      .map((symbol) => `event-contract=${encodeURIComponent(symbol)}`)
      .join("&")
    const helper = await this.marketDataClient.get(`by-type?${query}`)
    return parseItemsResponse(helper, MARKET_DATA_DESER.toParser(MarketData))
  }
}
